import React, {useMemo} from 'react';
import {StatusBar, View, Platform} from 'react-native';
import {
  CardStyleInterpolators,
  createStackNavigator,
} from '@react-navigation/stack';
import {NavigationContainer} from '@react-navigation/native';
import {SafeAreaView, SafeAreaProvider} from 'react-native-safe-area-context';
import MainNav from './main-navigation';
import theme from '@style';
import {linking, routes} from './route';
import {getUUID, getUrlParams, navigationRef, envConfig, goTo} from '@utils';
import globalStore from './services/global.state';
import {BasicObject, SafeAny} from '@types';
import {DialogLoading} from '@basicComponents/dialog';
import {useConfirm, useToast} from '@basicComponents/modal';
import {
  getBalance,
  getMessageNotReadCount,
  postReadMessage,
} from '@services/global.service';
import {debounceTime, Subject, takeUntil, throttleTime} from 'rxjs';
import {
  dailyRecord,
  initPush,
  setToken,
  setUserInfo,
  setVisitor,
} from './app.service';
import {useVersionModal} from './common-pages/hooks/versionmodal.hooks';
import {useTranslation} from 'react-i18next';
import {useLanguageModal} from './components/business/language';
import {defaultChannel} from './config';
import {ParabolaProvider} from './components/basic/parabola';
import {setupAdjust} from './utils/adjust';
import {inApp} from './utils/interface';
import {checkUpdate as checkUpdateNew} from './utils/update';
import DetailNavTitle from './components/business/detail-nav-title';
import Spin from './components/basic/spin';
import PushBox from './components/business/push-box/push-box';
import {ImBox} from '@/common-pages/im/im-box';
import {useChannelParams} from '@/pages/home/home.hooks';
import {VipUpgradeProvider} from './common-pages/vip/provider/vip-upgrade-provider';
import HomeSequential from '@/pages/home/components/home-sequential';
import {ModalManagerProvider} from '@basicComponents/sequential-modal/ModalManagerProvider';
import SharePanelProvider from '@/components/business/share-panel/share-panel-provider';

setVisitor(getUUID());

declare var CodePush: any;
declare var AppWithCodePush: any;
if (globalStore.isAndroid) {
  CodePush = require('react-native-code-push');
}
const Stack = createStackNavigator();
const params = getUrlParams();
const betByContainerStyle = {
  width: '100%',
  height: '100vh',
  display: 'none',
  flexDirection: 'column' as const,
  overflow: 'hidden' as const,
  position: 'absolute' as const,
  top: 0,
  left: 0,
};
const betByContentStyle = {flex: 1, overflow: 'auto' as const};
const ignoreRejectedRequest = (_error?: SafeAny) => undefined;

function App(): JSX.Element {
  if (params.channel) {
    globalStore.channel = params.channel;
  }
  if (params.code) {
    localStorage.setItem('invitationCode', params.code);
  }
  const appDistory = new Subject<boolean>();
  const {i18n} = useTranslation();
  const routeNameRef = React.useRef<null | string>();
  const remoteBundleRef = React.useRef<null | SafeAny>();
  const downloadLock = React.useRef<boolean>(false);
  const [currentRouteName, setCurrentRouteName] = React.useState('');
  const [globalLoading, setGlobalLoading] = React.useState(false);
  const {renderModal: renderToast, show: toastShow} = useToast();
  const {renderModal: renderDownloadToast, show: downloadToastShow} = useToast({
    backDropClose: false,
    delayClose: 5000,
  });
  const lanModalStatus = React.useRef(false);

  React.useEffect(() => {
    const sub = globalStore.openSports.subscribe(path => {
      // goTo(homePage, {screen: 'Sports'});
      goTo('Sports', {path});
    });
    return sub.unsubscribe;
  }, []);

  const initChat = () => {
    const chatModule = require('@components/chat');
    const freshchatConfig = new chatModule.FreshchatConfig(
      'cadd5056-e281-4e66-a5db-60760a4907e0',
      '571f4d41-60cd-433d-acde-4ecef3b88847',
    );
    freshchatConfig.domain = 'msdk.freshchat.com';
    chatModule.Freshchat.init(freshchatConfig);
  };

  const {renderModal: renderConfirmModal, show: confirmShow} = useConfirm(
    'Update Now',
    'Later',
  );

  /** 全局订阅 */
  const globalSubscriptions = () => {
    globalStore.globalLoading.pipe(takeUntil(appDistory)).subscribe(bool => {
      setGlobalLoading(bool);
    });
    globalStore.globalTotal.pipe(takeUntil(appDistory)).subscribe(config => {
      config.message = config.message;
      toastShow(config);
    });
    globalStore.tokenSubject.pipe(takeUntil(appDistory)).subscribe(token => {
      if (!token) {
        globalStore.notificationSubject.next({
          messageTotalCount: 0,
          sysMessageCount: 0,
          sysUserMessageCount: 0,
        });
        return;
      }
      setTimeout(() => {
        globalStore.updateAmount.next({});
      }, 100);
    });
    globalStore.refreshNotification
      .pipe(debounceTime(500), takeUntil(appDistory))
      .subscribe(() => {
        getMessageNotReadCount()
          .then(countInfo => globalStore.notificationSubject.next(countInfo))
          .catch(ignoreRejectedRequest);
      });
    globalStore.readNotification
      .pipe(takeUntil(appDistory))
      .subscribe(({messageId, messageType}) => {
        postReadMessage(messageId, messageType)
          .then(() => {
            globalStore.refreshNotification.next();
          })
          .catch(ignoreRejectedRequest);
      });
    globalStore.updateAmount
      .pipe(throttleTime(200), takeUntil(appDistory))
      .subscribe(({gameType}) => {
        globalStore.token &&
          getBalance(gameType)
            .then(res => {
              const {totalBalance} = res;
              const amount = totalBalance;
              globalStore.setAmount(amount);
            })
            .catch(ignoreRejectedRequest);
      });
  };

  const checkUpdate = async () => {
    if (__DEV__ || !envConfig.codePushKey || globalStore.isWeb) {
      return;
    }
    try {
      remoteBundleRef.current = await CodePush.checkForUpdate(
        envConfig.codePushKey,
      );
      setTimeout(() => {
        startBackDownload(remoteBundleRef.current);
      }, 5000);
      // await checkRemoteBoundle(remoteBundleRef.current);
    } catch (error) {
      console.error(error);
    }
  };

  const [chckedLang, setCheckLang] = React.useState(false);
  const {renderModal: renderLanguageModal, show: languageShow} =
    useLanguageModal({
      afterHidden: () => {
        setCheckLang(true);
        lanModalStatus.current = false;
      },
    });
  const checkLang = () => {
    globalStore.asyncGetItem('language').then(res => {
      if (res) {
        setCheckLang(true);
        globalStore.lang = res;
        i18n.changeLanguage(res);
      } else {
        languageShow();
        lanModalStatus.current = true;
      }
    });
  };

  const initApp = () => {
    setToken();
    setUserInfo();
    checkLang();
    globalSubscriptions();
    globalStore.asyncGetItem('last_record_time').then(res => {
      const timeCode = parseInt(res || '0', 10);
      if (
        !timeCode ||
        timeCode < new Date(new Date().toLocaleDateString()).getTime()
      ) {
        setTimeout(() => {
          dailyRecord();
          globalStore.asyncSetItem(
            'last_record_time',
            new Date().getTime() + '',
          );
        }, 600);
      }
    });
    if (globalStore.isWeb) {
      if (inApp) {
        checkUpdateNew();
      }
    }
    if (globalStore.isAndroid) {
      checkUpdate();
      initChat();
      initPush();
      setTimeout(() => {
        versionModal.handleUpdate();
      }, 1000);
      const ReactMoE = require('react-native-moengage').default;
      ReactMoE.setEventListener('pushClicked', (notificationPayload: any) => {
        /**
         * 点击PUSH事件回调在此处处理, 注意测试下该回调是在前台触发还是后台接收触发
         */
        console.info('pushClicked', notificationPayload);
      });
    }
  };

  React.useEffect(() => {
    globalStore.asyncGetItem('channel').then(c => {
      globalStore.channel = c || defaultChannel;
      globalStore.asyncSetItem('hasChecked', globalStore.channel);
      initApp();
    });
    return () => {
      appDistory.next(true);
      appDistory.complete();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startBackDownload = async (remoteBund: SafeAny) => {
    if (remoteBund) {
      downloadToastShow({
        type: 'weak',
        message: i18n.t('splash.tip.hasNew'),
      });
      downloadLock.current = true;
      const bundle = await remoteBund.download();
      await bundle.install(CodePush.InstallMode.ON_NEXT_RESTART);
      await CodePush.notifyAppReady();
      if (remoteBund.isMandatory) {
        confirmShow(
          i18n.t('splash.tip.alertTitle'),
          i18n.t('splash.tip.alertContent'),
          async () => {
            setTimeout(() => {
              CodePush.restartApp();
            }, 100);
          },
          () => {},
          true,
        );
      } else {
        downloadToastShow({
          type: 'weak',
          message: i18n.t('splash.tip.alertContent'),
        });
      }
    }
  };

  const versionModal = useVersionModal(
    true,
    versionInfo => {
      if (versionInfo) {
        // 如果需要更新,就不触发弹窗
        return;
      }
    },
    false,
  );
  const checkLangRef = React.useRef(false);
  React.useEffect(() => {
    checkLangRef.current = chckedLang;
    if (chckedLang) {
      checkUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chckedLang]);

  const betbyRender = useMemo(() => {
    return (
      <div style={betByContainerStyle} id="betby-container">
        <DetailNavTitle
          onBack={() => globalStore.betbyBackSubject.next('back')}
          hideServer
          title="Sports"
        />
        <div id="betby" style={betByContentStyle}>
          <Spin loading style={theme.fill.fill} />
        </div>
      </div>
    );
  }, []);
  useChannelParams();

  return (
    <SafeAreaProvider style={[theme.position.rel]}>
      <ModalManagerProvider>
        <React.Fragment>
          {Platform.OS === 'web' ? (
            <style type="text/css">{`
        @font-face {
          font-family: 'Inter';
          src: url('/assets/fonts/Inter.ttf') format('truetype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Inter-Bold';
          src: url('/assets/fonts/Inter-Bold.ttf') format('truetype');
          font-display: swap;
          font-weight: 700;
        }
        @font-face {
          font-family: 'DIN';
          src: url('/assets/fonts/din.ttf') format('truetype');
          font-display: swap;
        }
        @font-face {
          font-family: 'DIN';
          src: url('/assets/fonts/din-mid.ttf') format('truetype');
          font-display: swap;
          font-weight: 500;
        }
        @font-face {
          font-family: 'DIN';
          src: url('/assets/fonts/din-bold.ttf') format('truetype');
          font-display: swap;
          font-weight: 700;
        }
        @font-face {
          font-family: 'DIN';
          src: url('/assets/fonts/din-black.ttf') format('truetype');
          font-display: swap;
          font-weight: 900;
        }
        @font-face {
          font-family: 'din';
          src: url('/assets/fonts/din.ttf') format('truetype');
          font-display: swap;
        }
        @font-face {
          font-family: 'din-mid';
          src: url('/assets/fonts/din-mid.ttf') format('truetype');
          font-display: swap;
          font-weight: 500;
        }
        @font-face {
          font-family: 'din-bold';
          src: url('/assets/fonts/din-bold.ttf') format('truetype');
          font-display: swap;
          font-weight: 700;
        }
        @font-face {
          font-family: 'din-black';
          src: url('/assets/fonts/din-black.ttf') format('truetype');
          font-display: swap;
          font-weight: 900;
        }
        @font-face {
          font-family: 'Anybody';
          src: url('/assets/fonts/Anybody.ttf') format('truetype');
          font-display: swap;
        }
        @font-face {
          font-family: 'Technology';
          src: url('/assets/fonts/technology.ttf') format('truetype');
          font-display: swap;
        }
        @font-face {
          font-family: 'MaterialCommunityIcons';
          src: url(${require('react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf')}) format('truetype');
        }
        ::-webkit-scrollbar{
          display: none;
        }
      `}</style>
          ) : null}
        </React.Fragment>
        <StatusBar barStyle="light-content" />
        <SafeAreaView
          style={[
            globalStore.isWeb &&
              ({
                height: '100%',
                width: '100%',
                maxWidth: '500px',
                marginHorizontal: 'auto',
              } as BasicObject),
            theme.flex.col,
            // theme.overflow.hidden,
            theme.position.rel,
          ]}>
          <VipUpgradeProvider>
            <View style={[theme.fill.fill]}>
              {
                <NavigationContainer
                  onStateChange={() => {
                    const previousRouteName = routeNameRef.current;
                    const currentRoute = navigationRef!.getCurrentRoute()!.name;
                    if (previousRouteName !== '') {
                      setCurrentRouteName(currentRoute);
                    }
                    routeNameRef.current = currentRouteName;
                  }}
                  ref={navigationRef}
                  linking={linking}>
                  <Stack.Navigator
                    screenOptions={{
                      headerShown: false,
                      cardStyleInterpolator: globalStore.isWeb
                        ? undefined
                        : CardStyleInterpolators.forHorizontalIOS,
                    }}>
                    <Stack.Screen name="Index" component={MainNav} />
                    {Object.values(routes).map(route => (
                      <Stack.Screen
                        key={route.name}
                        name={route.name}
                        component={route.component}
                        options={{headerShown: route.headerShown || false}}
                      />
                    ))}
                  </Stack.Navigator>
                </NavigationContainer>
              }
              {betbyRender}
            </View>
          </VipUpgradeProvider>
        </SafeAreaView>
        <HomeSequential />
        <DialogLoading isVisible={globalLoading} />
        {renderToast}
        {renderDownloadToast}
        {versionModal.versionModal.renderModal}
        {/*{list.length > 0 && (*/}
        {/*  <PromotionModal*/}
        {/*    popVisible={popVisible}*/}
        {/*    setPopVisible={setPopVisible}*/}
        {/*    list={list}*/}
        {/*  />*/}
        {/*)}*/}
        {renderLanguageModal}
        {renderConfirmModal}
        <SharePanelProvider />

        <ParabolaProvider />
        <PushBox />
        <ImBox />
      </ModalManagerProvider>
    </SafeAreaProvider>
  );
}

if (globalStore.isAndroid) {
  let codePushOptions = {checkFrequency: CodePush.CheckFrequency.MANUAL};
  AppWithCodePush = CodePush(codePushOptions)(App);
}
if (globalStore.isWeb) {
  setupAdjust();
}

export default globalStore.isWeb ? App : AppWithCodePush;
